import _ from "lodash";
import React, { useState, useEffect } from "react";
import {
  BarChart,
  Legend,
  CartesianGrid,
  Tooltip,
  Bar,
  XAxis,
  YAxis,
} from "recharts";

import {
  OrgDailyMetricsSettingData,
  OrgDailyMetricsData,
} from "../../../../models/MetricsLineChartData";
import "./styles.scss";

export interface IBarChartItemProps {
  width: number;
  height: number;
  dataList: OrgDailyMetricsData;
}

export const BarChartItem: React.FunctionComponent<IBarChartItemProps> = (
  props
) => {
  const { width, height, dataList } = props;

  const [dataListShown, setDataListShown] = useState<OrgDailyMetricsData>();
  const [useStaggeredLabels, setUseStaggeredLabels] = useState<boolean>(false);

  useEffect(() => {
    if (dataList) {
      const dataListTemp = dataList;

      dataListTemp.dataForLine.forEach((item) => {
        const keys = Object.keys(item);
        keys.forEach((itemKey) => {
          if (itemKey !== "name" && item[itemKey]) {
            item[itemKey] = parseFloat(item[itemKey]);
          }
        });
      });

      setDataListShown(_.cloneDeep(dataListTemp));

      // Check if labels would overlap
      const dataCount = dataListTemp.dataForLine.length;
      if (dataCount > 0) {
        const avgLabelLength =
          dataListTemp.dataForLine.reduce(
            (sum, item) => sum + (item.name?.toString().length || 0),
            0
          ) / dataCount;
        // Estimate space per bar (in pixels)
        const spacePerBar = width / dataCount;
        // Estimate label width (assuming ~8px per character with fontSize 13)
        const estimatedLabelWidth = avgLabelLength * 8;

        // Use staggered labels if labels would overlap (with some padding)
        setUseStaggeredLabels(estimatedLabelWidth > spacePerBar * 0.5);
      }
    }
  }, [dataList, width]);

  // Custom tick component for staggered labels
  const CustomTick = (props: any) => {
    const { x, y, payload, index } = props;
    // Alternate between two vertical positions
    const dy = index % 2 === 0 ? 10 : 30;

    return (
      <g transform={`translate(${x},${y})`}>
        <text
          x={0}
          y={0}
          dy={dy}
          textAnchor="middle"
          fill="#666"
          fontSize={13}
          fontFamily="Inter"
        >
          {payload.value}
        </text>
      </g>
    );
  };

  return (
    <>
      {!!dataListShown && (
        <BarChart
          width={width}
          height={height}
          data={dataListShown.dataForLine}
        >
          <Legend
            iconType="plainline"
            wrapperStyle={{ fontSize: "12px", fontWeight: 700 }}
          />
          <CartesianGrid strokeDasharray="3 2" />
          <XAxis
            dataKey="name"
            tick={useStaggeredLabels ? <CustomTick /> : { fontSize: 13 }}
            height={useStaggeredLabels ? 60 : 30}
            style={{ fontFamily: "Inter" }}
            interval={0}
          />
          <YAxis
            tick={{ fontSize: 10 }}
            tickCount={6}
            style={{ fontFamily: "Inter" }}
            tickFormatter={(value: number) =>
              new Intl.NumberFormat("en").format(value)
            }
          />
          <Tooltip
            formatter={(value) =>
              new Intl.NumberFormat("en").format(value as number)
            }
            labelFormatter={(value) => {
              return `${value}`;
            }}
          />
          {dataListShown.dataSources.map(
            (item: OrgDailyMetricsSettingData, index: number) => (
              <React.Fragment key={index}>
                <Bar
                  name={item.label}
                  dataKey={item.fieldName}
                  fill={item.color}
                  barSize={40}
                />
              </React.Fragment>
            )
          )}
        </BarChart>
      )}
    </>
  );
};

export default BarChartItem;
